/**
 * <marsello-cute> dresses the Marsello "Sweet Rewards" launcher and window in the pinky look.
 * Marsello draws both inside same-site frames (#marsello-tab, #marsello-widget) that page CSS
 * can't reach, so this copies the theme's fonts and colours into each frame as a style tag.
 */
const FRAME_IDS = ['marsello-widget', 'marsello-tab'];
const STYLE_ID = 'ccc-cute-rewards';

const CUTE_CSS = `
html, body { background: transparent !important; }
body { font-family: var(--ccc-body) !important; color: var(--ccc-ink) !important; }

/* Launcher button: always a "My Rewards" pill, whatever tab style Marsello is set to */
html[data-ccc-frame="marsello-tab"], html[data-ccc-frame="marsello-tab"] body { margin: 0 !important; padding: 0 !important; height: 100%; overflow: hidden; }
.marsello-tab {
  box-sizing: border-box !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 16px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: #FBD3E0 !important;
  color: #B0124F !important;
  border: 2px solid #B0124F !important;
  border-radius: 999px !important;
  outline: 2px dashed #E7739F;
  outline-offset: -6px;
  cursor: pointer;
}
.marsello-tab > * { display: none !important; }
.marsello-tab::after {
  content: "My Rewards";
  color: #B0124F !important;
  font-family: var(--ccc-heading) !important;
  font-weight: 800;
  font-size: 18px;
  line-height: 1;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
.marsello-tab:hover { filter: brightness(1.07); }
/* Little treats floating inside the pill, like the home page's falling treats */
.marsello-tab { position: relative !important; overflow: hidden !important; }
.marsello-tab::before {
  content: "";
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 176 52' preserveAspectRatio='none'%3E%3Cg transform='translate(10 8) scale(.3)'%3E%3Cpath d='M24 42S6 31 6 18a9 9 0 0 1 18-3 9 9 0 0 1 18 3c0 13-18 24-18 24z' fill='%23D91F6B'/%3E%3C/g%3E%3Cg transform='translate(150 6) scale(.5)'%3E%3Cpath d='M12 0C13 8 16 11 24 12C16 13 13 16 12 24C11 16 8 13 0 12C8 11 11 8 12 0Z' fill='%23F7A93E'/%3E%3C/g%3E%3Crect x='154' y='34' width='10' height='3.4' rx='1.7' transform='rotate(35 159 35.7)' fill='%23D91F6B'/%3E%3Cg transform='translate(22 33) scale(.32)'%3E%3Cpath d='M12 0C13 8 16 11 24 12C16 13 13 16 12 24C11 16 8 13 0 12C8 11 11 8 12 0Z' fill='%23E7739F'/%3E%3C/g%3E%3Crect x='30' y='12' width='8' height='3' rx='1.5' transform='rotate(-30 34 13.5)' fill='%23F7A93E'/%3E%3Cg transform='translate(138 36) scale(.22)'%3E%3Cpath d='M24 42S6 31 6 18a9 9 0 0 1 18-3 9 9 0 0 1 18 3c0 13-18 24-18 24z' fill='%23E7739F'/%3E%3C/g%3E%3C/svg%3E") center / 100% 100% no-repeat;
  pointer-events: none;
}
@media (prefers-reduced-motion: no-preference) {
  @keyframes ccc-treats-twinkle {
    0%, 100% { transform: translateY(1px); opacity: 1; }
    50% { transform: translateY(-3px); opacity: 0.7; }
  }
  .marsello-tab::before { animation: ccc-treats-twinkle 2.8s ease-in-out infinite; }
}
/* The launcher frame shrinks on phones, so the label shrinks with it. */
@media (max-width: 160px) {
  .marsello-tab::after { font-size: 15px; }
  .marsello-tab { outline-offset: -5px; }
}

/* Window */
.widget__content, .widget__body, .tabContent, .frame-content > div { background: var(--ccc-cream) !important; }
.header h1, .widget .title, h1, h2 {
  font-family: var(--ccc-heading) !important;
  color: var(--ccc-pink) !important;
  font-weight: 800 !important;
  letter-spacing: -0.01em;
}
.header h1 { font-size: 2.1em !important; }
.header p, .text, .description { color: var(--ccc-ink) !important; }

/* Tabs become pills */
.tab-nav {
  background: var(--ccc-blush) !important;
  border: 0 !important;
  border-radius: 999px !important;
  padding: 5px !important;
}
.tab-nav > * {
  border: 0 !important;
  border-radius: 999px !important;
  color: var(--ccc-ink) !important;
  font-family: var(--ccc-heading) !important;
  font-weight: 700 !important;
}
.tab-nav > .active { background: var(--ccc-pink) !important; color: #fff !important; }

/* Reward rows become little cards */
.list > * {
  background: #fff !important;
  border: 2px solid var(--ccc-border) !important;
  border-radius: 18px !important;
  margin: 10px 0 !important;
  padding: 12px 18px !important;
  color: var(--ccc-ink) !important;
}
.points, .list > * > :last-child { color: var(--ccc-pink) !important; font-weight: 700 !important; }
.icon span { color: var(--ccc-pink) !important; }

/* Buttons and links */
.btn {
  background: var(--ccc-pink) !important;
  color: #fff !important;
  border: 0 !important;
  border-radius: 999px !important;
  font-family: var(--ccc-heading) !important;
  font-weight: 800 !important;
  padding: 14px 32px !important;
  box-shadow: 0 5px 0 var(--ccc-pink-deep) !important;
}
.btn:hover { filter: brightness(1.06); }
.footer p, .footer p a { color: var(--ccc-ink) !important; }
.footer p a { color: var(--ccc-pink) !important; }
input { border-radius: 14px !important; border: 2px solid var(--ccc-border) !important; }

/* Close (X) button: a simple cream circle with a pink X tucked inside the rounded top-right corner.
   Resets the pill .btn look so it never turns into a big pink button. */
.close, .btn.close, .widget__close, .close-button {
  position: absolute !important;
  top: 14px !important;
  right: 14px !important;
  z-index: 10 !important;
  box-sizing: border-box !important;
  width: 30px !important;
  height: 30px !important;
  min-width: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: var(--ccc-cream) !important;
  color: var(--ccc-pink) !important;
  border: 1.5px solid var(--ccc-pink) !important;
  border-radius: 999px !important;
  box-shadow: none !important;
  font-family: var(--ccc-body) !important;
  font-size: 0 !important;
  color: transparent !important;
  line-height: 1 !important;
  text-indent: 0 !important;
  opacity: 1 !important;
  cursor: pointer !important;
  transition: background 0.15s ease;
}
.close:hover, .close:focus-visible { background: var(--ccc-blush) !important; }
.close:focus-visible { outline: 2px solid var(--ccc-pink) !important; outline-offset: 2px !important; }
/* Marsello's own X is hidden; the theme draws a bold pink X from two rounded bars. */
.close > *, .btn.close > *, .widget__close > *, .close-button > * { display: none !important; }
.close::before, .close::after,
.btn.close::before, .btn.close::after,
.widget__close::before, .widget__close::after,
.close-button::before, .close-button::after {
  content: "" !important;
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  width: 14px !important;
  height: 3px !important;
  margin: 0 !important;
  background: var(--ccc-pink) !important;
  border-radius: 3px !important;
}
.close::before, .btn.close::before, .widget__close::before, .close-button::before { transform: translate(-50%, -50%) rotate(45deg) !important; }
.close::after, .btn.close::after, .widget__close::after, .close-button::after { transform: translate(-50%, -50%) rotate(-45deg) !important; }
`;

class MarselloCute extends HTMLElement {
  connectedCallback() {
    this.timer = setInterval(() => this.dress(), 800);
    this.dress();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  themeVars() {
    const styles = getComputedStyle(document.body);
    const read = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;
    const pink = read('--color-interactive', '#D91F6B');
    const label = (this.dataset.label || 'My Rewards').replace(/["\\]/g, '');
    return `:root {
  --ccc-label: "${label}";
  --ccc-pink: ${pink};
  --ccc-pink-deep: color-mix(in oklab, ${pink} 70%, #000);
  --ccc-ink: ${read('--color-on-surface', '#3D1F16')};
  --ccc-cream: ${read('--color-surface', '#FFF4EE')};
  --ccc-blush: ${read('--color-surface-secondary', '#FBD3E0')};
  --ccc-border: ${read('--color-border', '#F2C9D3')};
  --ccc-heading: ${read('--font-heading', 'Georgia, serif')};
  --ccc-body: ${read('--font-body', 'sans-serif')};
}`;
  }

  fontFaces() {
    return Array.from(document.querySelectorAll('style'))
      .map((style) => style.textContent || '')
      .filter((text) => text.includes('@font-face'))
      .join('\n');
  }

  dress() {
    FRAME_IDS.forEach((id) => {
      const frame = document.getElementById(id);
      if (!frame) return;
      let doc;
      try {
        doc = frame.contentDocument;
      } catch (error) {
        return;
      }
      if (!doc || !doc.head || doc.getElementById(STYLE_ID)) return;
      const style = doc.createElement('style');
      style.id = STYLE_ID;
      doc.documentElement.dataset.cccFrame = id;
      const label = (this.dataset.label || "My Rewards").replace(/["\\]/g, "");
      const css = CUTE_CSS.replace("content: \"My Rewards\";", `content: "${label}";`);
      style.textContent = `${this.fontFaces()}\n${this.themeVars()}\n${css}`;
      doc.head.appendChild(style);
    });
  }
}

if (!customElements.get('marsello-cute')) customElements.define('marsello-cute', MarselloCute);
